import { apiClient } from "@/lib/api";
import type { PaginationParams } from "@/types";

export type AdminNotificationSeverity = "warning" | "error" | "critical";
export type AdminNotificationStatus = "open" | "acknowledged" | "resolved";
export type AdminNotificationCategory =
  | "payment"
  | "booking"
  | "receipt"
  | "scheduler"
  | "integration"
  | "support"
  | "device";

export interface AdminNotification {
  id: string;
  category: string;
  severity: AdminNotificationSeverity;
  status: AdminNotificationStatus;
  title: string;
  body: string;
  event_type: string;
  target_type: string | null;
  target_id: string | null;
  dedupe_key: string;
  occurrence_count: number;
  last_occurred_at: string;
  metadata_json: Record<string, unknown> | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  acknowledged_by_email: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminNotificationListResponse {
  total: number;
  items: AdminNotification[];
}

export interface AdminNotificationStatusCounts {
  open: number;
  acknowledged: number;
  resolved: number;
  all: number;
}

export interface StatusCountParams {
  severity?: AdminNotificationSeverity;
  category?: AdminNotificationCategory;
}

export interface ListAdminNotificationsParams extends PaginationParams {
  status?: AdminNotificationStatus;
  severity?: AdminNotificationSeverity;
  category?: AdminNotificationCategory;
}

function toTimestamp(value: string): number {
  return new Date(value).getTime();
}

function sortNewestFirst(items: AdminNotification[]): AdminNotification[] {
  return [...items].sort(
    (left, right) =>
      toTimestamp(right.last_occurred_at) - toTimestamp(left.last_occurred_at),
  );
}

export const adminNotificationService = {
  async list(
    params: ListAdminNotificationsParams = {},
  ): Promise<AdminNotificationListResponse> {
    const { data } = await apiClient.get<AdminNotificationListResponse>(
      "/admin/notifications",
      { params },
    );
    return {
      ...data,
      items: sortNewestFirst(data.items),
    };
  },

  async getStatusCounts(
    params: StatusCountParams = {},
  ): Promise<AdminNotificationStatusCounts> {
    const { data } = await apiClient.get<AdminNotificationStatusCounts>(
      "/admin/notifications/status-counts",
      { params },
    );
    return data;
  },

  async acknowledge(notificationId: string): Promise<AdminNotification> {
    const { data } = await apiClient.post<AdminNotification>(
      `/admin/notifications/${notificationId}/acknowledge`,
    );
    return data;
  },

  async resolve(notificationId: string): Promise<AdminNotification> {
    const { data } = await apiClient.post<AdminNotification>(
      `/admin/notifications/${notificationId}/resolve`,
    );
    return data;
  },

  async reopen(notificationId: string): Promise<AdminNotification> {
    const { data } = await apiClient.post<AdminNotification>(
      `/admin/notifications/${notificationId}/reopen`,
    );
    return data;
  },
};
